import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import router from '@/router'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from localStorage (persisted by Pinia)
    const authData = localStorage.getItem('health-monitor-auth')
    if (authData) {
      try {
        const parsed = JSON.parse(authData)
        if (parsed.token) {
          config.headers.Authorization = `Bearer ${parsed.token}`
        }
      } catch (e) {
        // Invalid JSON, ignore
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      const status = error.response.status

      // Handle 401 Unauthorized - redirect to login
      if (status === 401) {
        localStorage.removeItem('health-monitor-auth')
        router.push({ name: 'login' })
      }

      // Handle 403 Forbidden
      if (status === 403) {
        console.error('Access forbidden:', error.response.data)
      }

      // Handle 422 Validation errors
      if (status === 422) {
        const data = error.response.data as { errors?: Record<string, string[]>; message?: string }
        if (data.errors) {
          // Transform Laravel validation errors to a more usable format
          const errors: Record<string, string> = {}
          for (const [key, messages] of Object.entries(data.errors)) {
            errors[key] = messages[0]
          }
          return Promise.reject({ ...error, validationErrors: errors })
        }
      }

      // Handle 500 Server errors
      if (status >= 500) {
        console.error('Server error:', error.response.data)
      }
    }

    return Promise.reject(error)
  }
)

export { api }

// Type-safe API methods
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  register: (data: {
    name: string
    email: string
    password: string
    password_confirmation: string
  }) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  getUser: () => api.get('/auth/user'),
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),
  sendVerificationEmail: () => api.post('/auth/email/send'),
  verifyEmail: (code: string) => api.post('/auth/email/verify', { code }),
  getVerificationStatus: () => api.get('/auth/verification-status'),
}

export const healthRecordsApi = {
  list: (params?: Record<string, any>) =>
    api.get('/health-records', { params }),
  get: (id: number) => api.get(`/health-records/${id}`),
  create: (data: Record<string, any>) => api.post('/health-records', data),
  update: (id: number, data: Record<string, any>) =>
    api.put(`/health-records/${id}`, data),
  delete: (id: number) => api.delete(`/health-records/${id}`),
  stats: (params?: Record<string, any>) =>
    api.get('/health-records/stats', { params }),
  byMetric: (metricTypeId: number, params?: Record<string, any>) =>
    api.get(`/health-records/by-metric/${metricTypeId}`, { params }),
  report: (params: Record<string, any>) =>
    api.get('/health-records/report', { params }),
  reportPdf: (params: Record<string, any>) =>
    api.get('/health-records/report/pdf', { params, responseType: 'blob' }),
  reportCsv: (params: Record<string, any>) =>
    api.get('/health-records/report/csv', { params, responseType: 'blob' }),
}

export const metricTypesApi = {
  list: () => api.get('/metric-types'),
  get: (id: number) => api.get(`/metric-types/${id}`),
  create: (data: Record<string, any>) => api.post('/metric-types', data),
  update: (id: number, data: Record<string, any>) =>
    api.put(`/metric-types/${id}`, data),
  delete: (id: number) => api.delete(`/metric-types/${id}`),
  getSubTypes: (id: number) => api.get(`/metric-types/${id}/sub-types`),
}

export const medicationsApi = {
  list: (params?: Record<string, any>) => api.get('/medications', { params }),
  current: (params?: Record<string, any>) =>
    api.get('/medications/current', { params }),
  get: (id: number) => api.get(`/medications/${id}`),
  create: (data: Record<string, any>) => api.post('/medications', data),
  update: (id: number, data: Record<string, any>) =>
    api.put(`/medications/${id}`, data),
  delete: (id: number) => api.delete(`/medications/${id}`),
  toggle: (id: number) => api.put(`/medications/${id}/toggle`),
}

export const prescriptionsApi = {
  list: (params?: Record<string, any>) => api.get('/prescriptions', { params }),
  get: (id: number) => api.get(`/prescriptions/${id}`),
  create: (data: FormData) =>
    api.post('/prescriptions', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  update: (id: number, data: FormData) =>
    api.put(`/prescriptions/${id}`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  delete: (id: number) => api.delete(`/prescriptions/${id}`),
}

export const testReportsApi = {
  list: (params?: Record<string, any>) => api.get('/test-reports', { params }),
  testTypes: () => api.get('/test-reports/test-types'),
  get: (id: number) => api.get(`/test-reports/${id}`),
  create: (data: FormData) =>
    api.post('/test-reports', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  update: (id: number, data: FormData) =>
    api.put(`/test-reports/${id}`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  delete: (id: number) => api.delete(`/test-reports/${id}`),
}

export const remindersApi = {
  list: (params?: Record<string, any>) => api.get('/reminders', { params }),
  get: (id: number) => api.get(`/reminders/${id}`),
  create: (data: Record<string, any>) => api.post('/reminders', data),
  update: (id: number, data: Record<string, any>) =>
    api.put(`/reminders/${id}`, data),
  delete: (id: number) => api.delete(`/reminders/${id}`),
  toggle: (id: number) => api.put(`/reminders/${id}/toggle`),
  snooze: (id: number, minutes: number) =>
    api.post(`/reminders/${id}/snooze`, { minutes }),
}

export const notificationsApi = {
  list: (params?: Record<string, any>) =>
    api.get('/notifications', { params }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id: number) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/mark-all-read'),
  updateFcmToken: (token: string) =>
    api.post('/notifications/fcm-token', { fcm_token: token }),
  delete: (id: number) => api.delete(`/notifications/${id}`),
  clearAll: () => api.delete('/notifications/clear-all'),
}

export const dashboardApi = {
  get: (params?: Record<string, any>) => api.get('/dashboard', { params }),
  quickStats: (params?: Record<string, any>) =>
    api.get('/dashboard/quick-stats', { params }),
  chartData: (params?: Record<string, any>) =>
    api.get('/dashboard/chart-data', { params }),
}

export const usersApi = {
  accessible: () => api.get('/users/accessible'),
  updateProfile: (data: FormData) =>
    api.post('/auth/user', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  changePassword: (data: Record<string, any>) =>
    api.put('/auth/password', data),
  // Dependents
  dependents: () => api.get('/users/dependents'),
  createDependent: (data: Record<string, any>) =>
    api.post('/users/dependents', data),
  updateDependent: (id: number, data: Record<string, any>) =>
    api.put(`/users/dependents/${id}`, data),
  deleteDependent: (id: number) => api.delete(`/users/dependents/${id}`),
  // Shares
  shares: () => api.get('/users/shares'),
  inviteShare: (data: Record<string, any>) =>
    api.post('/users/shares/invite', data),
  respondToShare: (id: number, status: 'accepted' | 'rejected') =>
    api.put(`/users/shares/${id}/respond`, { status }),
  updateShare: (id: number, data: Record<string, any>) =>
    api.put(`/users/shares/${id}`, data),
  deleteShare: (id: number) => api.delete(`/users/shares/${id}`),
}

// Shorthand API object for easier use in components
const apiService = {
  // Auth
  auth: authApi,

  // Health Records
  healthRecords: healthRecordsApi,

  // Metric Types
  metricTypes: metricTypesApi,

  // Medications
  medications: medicationsApi,

  // Prescriptions
  prescriptions: prescriptionsApi,

  // Test Reports
  testReports: testReportsApi,

  // Reminders
  reminders: remindersApi,

  // Notifications
  notifications: {
    ...notificationsApi,
    markRead: notificationsApi.markAsRead,
  },

  // Dashboard
  dashboard: dashboardApi,

  // Users (accessible users, dependents, shares)
  users: usersApi,

  // Dependents (shorthand)
  dependents: {
    list: usersApi.dependents,
    create: usersApi.createDependent,
    update: usersApi.updateDependent,
    delete: usersApi.deleteDependent,
  },

  // Shares (shorthand)
  shares: {
    list: usersApi.shares,
    invite: usersApi.inviteShare,
    respond: usersApi.respondToShare,
    update: usersApi.updateShare,
    revoke: usersApi.deleteShare,
  },
}

export default apiService
